import React from "react"
import { useParams, useRouteLoaderData } from "react-router-dom"

import {
  Box,
  Flex,
  Button,
  Alert,
  Text,
  HStack,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  CircularProgress,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
} from "@chakra-ui/react"
import { DataTable } from "./DataTable"
import UploadRecords from "./UploadRecords"
import DeleteConfirmModal from "./DeleteConfirmModal"
import {
  HashedSchemas,
  Schemas,
  UploadProgress,
  FileName,
  IsUploading,
  UploadStatus,
  Limit,
  Offset,
  CurrentFilters,
  SchemaRecordCounts,
} from "../lib/store"
import useFileStore from "../lib/fileStore"

import { Api } from "../lib/api"
import { isEmpty, unrollError } from "../lib/util"
import { FaUpload } from "react-icons/fa6"
import IconLock from "@/icons/IconLock"
import { addAlert, AlertType, removeAlertBySource } from "@/lib/alerts"
import TableFilter from "./TableFilter"
import { MAX_STRING_FILTER_COMBINATIONS } from "@/lib/limits"

const SYSTEM_LIMITS_BANNER_KEY =
  "blindinsight_system_limits_records_banner_dismissed"

export default function SchemaRecordsList() {
  const [limit] = Limit.useState()
  const [offset] = Offset.useState()
  const [isLoading, setIsLoading] = React.useState(false)
  const isLoadingRef = React.useRef(false)
  const user = useRouteLoaderData("auth")
  const { schemaId } = useParams()
  const api = React.useMemo(() => new Api(), [])

  // Initialize state-pool values
  const [schemas] = Schemas.useState()
  const [schemaRecordCounts] = SchemaRecordCounts.useState()
  const [currentFilters] = CurrentFilters.useState()
  const [hashedSchemas] = HashedSchemas.useState()
  const [uploadProgress, setUploadProgress] = UploadProgress.useState()
  const [, setFileName] = FileName.useState()
  const [isUploading, setIsUploading] = IsUploading.useState()
  const clearSelectedFile = useFileStore((state) => state.clearSelectedFile)
  const [deleteRecordId, setDeleteRecordId] = React.useState(null)
  const [deleteRecordsOpen, setDeleteRecordsOpen] = React.useState(false)

  // List of current records in view
  const [records, setRecords] = React.useState([])
  // Total record count from server (X-Total-Count), kept in state so it isn't lost
  const [serverTotalCount, setServerTotalCount] = React.useState(null)
  // Whether there are more pages of filtered results beyond the current page
  const [hasMore, setHasMore] = React.useState(false)
  // Background count fetch state — tracked separately so records render
  // immediately while the total count resolves asynchronously.
  const [isCountLoading, setIsCountLoading] = React.useState(false)
  // Monotonic counter to discard stale count responses after filter changes
  const countRequestRef = React.useRef(0)

  // Find the selected schema
  const schema = React.useMemo(() => schemas[schemaId], [schemas, schemaId])
  // Get the hashed (decrypted) schema
  const hashedSchema = React.useMemo(
    () => hashedSchemas[schemaId],
    [hashedSchemas, schemaId],
  )
  const schemaHasNoRecords = schemaRecordCounts[schemaId] === 0

  // Decryption toggle
  const [encrypted, setEncrypted] = React.useState(true)
  // Can decrypt schema?
  const [canDecryptSchema, setCanDecryptSchema] = React.useState(true)

  // Can decrypt records?
  const [canDecryptRecords, setCanDecryptRecords] = React.useState(false)
  const canDecryptCheckedRef = React.useRef(null)

  // Reset canDecryptRecords if the schema changes or decryption becomes unavailable.
  // The actual per-record check fires inline inside updateRecords.
  React.useEffect(() => {
    if (!canDecryptSchema) setCanDecryptRecords(false)
  }, [canDecryptSchema])
  const count = React.useMemo(() => {
    if (serverTotalCount != null) return serverTotalCount
    return -1
  }, [serverTotalCount])

  // Records to decrypt
  const [missingRecords, setMissingRecords] = React.useState({})
  // Decrypted records
  const [decryptedRecords, setDecryptedRecords] = React.useState({})
  // Records pending decryption
  const [pendingRecords, setPendingRecords] = React.useState({})
  // Schema pending decryption
  const [pendingSchema, setPendingSchema] = React.useState(false)

  const properties = React.useMemo(
    () => schema?.schema?.properties || {},
    [schema],
  )

  const recordMap = React.useMemo(() => {
    if (pendingSchema) return
    let inverted = {}
    for (const record of records) {
      inverted[record.id] = record.data
    }
    if (encrypted) return inverted

    let changed = false
    let missing = {}
    for (const id in missingRecords) {
      missing[id] = missingRecords[id]
    }
    for (const record of records) {
      if (!decryptedRecords[record.id]) {
        if (pendingRecords[record.id]) continue
        if (!missing[record.id]) {
          changed = true
          missing[record.id] = record
        }
      }
    }
    if (changed) setMissingRecords(missing)

    return decryptedRecords
  }, [
    records,
    decryptedRecords,
    missingRecords,
    pendingRecords,
    pendingSchema,
    encrypted,
  ])

  // Decrypt the schema eagerly — fires on mount in parallel with the records fetch.
  // Schema decryption doesn't need records, so we don't wait for them.
  React.useEffect(() => {
    if (!schema) return
    if (!canDecryptSchema) return
    if (pendingSchema) return
    if (hashedSchemas[schema.id]) return
    setPendingSchema(true)
    api
      .schemasDecrypt(schema)
      .then(() => setPendingSchema(false))
      .catch(() => {
        setPendingSchema(false)
        setCanDecryptSchema(false)
      })
  }, [
    hashedSchemas,
    schema,
    pendingSchema,
    setPendingSchema,
    canDecryptSchema,
    setCanDecryptSchema,
  ])

  const PROXY_CACHE_KEY = "BI_HAS_PROXY"
  const [hasProxy, setHasProxy] = React.useState(
    () => sessionStorage.getItem(PROXY_CACHE_KEY) === "true",
  )

  React.useEffect(() => {
    api.statusRetrieve().then((resp) => {
      const behind = Boolean(resp?.behind_proxy)
      sessionStorage.setItem(PROXY_CACHE_KEY, behind)
      setHasProxy(behind)
    })
  }, [])

  // This effect will attempt to decrypt the records if "encrypted" is false.
  // If it fails, it should revert to displaying the encrypted records.
  React.useEffect(() => {
    // If we're not decrypting, get out early
    if (encrypted) return

    // If we aren't allowed to decrypt, get out early
    if (!canDecryptRecords) return

    // Calculate actual missing records vs what is pending in an API query
    let missing = []
    if (Object.keys(missingRecords).length > 0) {
      let pending = {}
      for (const id in missingRecords) {
        if (!pendingRecords[id]) {
          missing.push(missingRecords[id])
          pending[id] = true
        }
      }
      for (const id in pendingRecords) {
        pending[id] = true
      }

      // If we didn't find any changes, get out
      if (missing.length === 0) return

      setMissingRecords({})
      setPendingRecords(pending)
    }

    if (missing.length === 0) return

    api
      .recordsDecrypt(missing)
      .then((data) => {
        let newDecrypted = {}
        for (const record of data) {
          newDecrypted[record.id] = record.data
        }
        for (const id in decryptedRecords) {
          newDecrypted[id] = decryptedRecords[id]
        }
        setDecryptedRecords(newDecrypted)
      })
      .catch((error) => {
        console.error("Error decrypting records", error)
        setMissingRecords({})
        if (error?.status === 404) {
          console.error("Decrypt records not found")
          addAlert(
            AlertType.ERROR,
            "Decrypted records not found",
            "Decrypted records not found, please refresh the page.",
          )
          return
        }
        setEncrypted(true)
        if ([403, 422].includes(error?.status)) {
          console.error("Decrypt permission denied")
          addAlert(
            AlertType.ERROR,
            "Permission Denied",
            "You do not have permission to decrypt these records.",
            10,
          )
          return setCanDecryptRecords(false)
        }
        // TODO(shakefu): This 405 is a wonky status code.
        if (error?.status === 405) {
          console.error("Decrypt not available on API")
        }
      })
  }, [
    encrypted,
    setEncrypted,
    canDecryptRecords,
    setCanDecryptRecords,
    missingRecords,
    setMissingRecords,
    decryptedRecords,
    setDecryptedRecords,
    pendingRecords,
    setPendingRecords,
  ])

  // Render nothing if we don't have a user
  if (!user) return null
  // Render a not found if we couldn't find a schema in state
  if (!schema) {
    addAlert(AlertType.ERROR, "Schema Not Found", "Schema not found")
  }

  const toggleEncryption = React.useCallback(() => {
    if (!canDecryptRecords && encrypted) return
    setEncrypted(!encrypted)
  }, [encrypted, canDecryptRecords, setEncrypted])

  const fieldNames = React.useMemo(() => {
    if (!recordMap) return []

    if (!hashedSchema || isEmpty(hashedSchema)) {
      // Flatten the recordMap to get the field names and remove duplicates as a set.
      const encryptedKeys = Object.values(recordMap).map((record) =>
        Object.keys(record),
      )
      const flattenedKeys = encryptedKeys.flat()
      const uniqueKeys = new Set(flattenedKeys)
      return Array.from(uniqueKeys)
    } else {
      return Object.keys(hashedSchema || {}).map((key) => {
        if (encrypted) return key
        return hashedSchema[key]?.name
      })
    }
  }, [recordMap, hashedSchema, encrypted])

  // missing here is the idField in the row - JP just can't
  const columns = React.useMemo(() => {
    return fieldNames.map((name) => ({
      accessorKey: name,
      header: name,
      field: name,
      type: properties[name]?.type,
    }))
  }, [fieldNames, properties])

  const data = React.useMemo(() => {
    return records.map((record) => {
      if (encrypted) {
        return { id: record.id, ...record.data }
      }
      return { id: record.id, ...decryptedRecords[record.id] }
    })
  }, [records, decryptedRecords, encrypted])

  function checkForFilteredRecords(schemaId, count) {
    const recordCounts = SchemaRecordCounts.getValue()
    SchemaRecordCounts.setValue({
      ...recordCounts,
      [schemaId]: count,
    })
  }

  // Fire once per schema when the first batch of records arrives.
  // Runs inline inside updateRecords to avoid waiting for a render cycle.
  const checkCanDecrypt = React.useCallback(
    (list) => {
      if (!list?.length) return
      if (canDecryptCheckedRef.current === schemaId) return
      canDecryptCheckedRef.current = schemaId
      api
        .recordsCanDecrypt(list[0])
        .then((resp) => setCanDecryptRecords(resp.can_decrypt))
        .catch((error) => {
          console.error("Error checking if record can be decrypted", error)
          setCanDecryptRecords(false)
          canDecryptCheckedRef.current = null
        })
    },
    [schemaId, api],
  )

  const updateRecords = React.useCallback(
    async (signal) => {
      isLoadingRef.current = true
      setIsLoading(true)
      setServerTotalCount(null)
      const t0 = performance.now()
      try {
        let resp
        if (currentFilters.length > 0) {
          resp = await api.recordsSearch(
            schemaId,
            currentFilters,
            limit,
            offset,
            signal,
          )
          if (signal?.aborted) return
          const list = Array.isArray(resp) ? resp : resp?.results ?? []
          setHasMore(list.length >= limit)
          setRecords(list)
          checkCanDecrypt(list)
          if (typeof resp?.count === "number" && !Number.isNaN(resp.count)) {
            setServerTotalCount(resp.count)
          } else if (resp?.xQuery) {
            // Records arrived without a total count — fire a lightweight
            // count-only request in the background using the server's X-Query
            // header so the pagination total resolves without blocking the UI.
            const requestId = ++countRequestRef.current
            setIsCountLoading(true)
            api
              .recordsCount(resp.xQuery)
              .then((countResp) => {
                if (requestId !== countRequestRef.current) return
                const n = countResp?.count
                if (typeof n === "number" && !Number.isNaN(n)) {
                  setServerTotalCount(n)
                }
              })
              .catch((err) => {
                console.warn("Background count request failed", err)
              })
              .finally(() => {
                if (requestId === countRequestRef.current) {
                  setIsCountLoading(false)
                }
              })
          }
        } else {
          resp = await api.recordsList(schemaId, limit, offset, signal)
          if (signal?.aborted) return
          setHasMore(false)
          const list = Array.isArray(resp) ? resp : resp?.results ?? []
          setRecords(list)
          checkCanDecrypt(list)
          setServerTotalCount(
            typeof resp?.count === "number" && !Number.isNaN(resp.count)
              ? resp.count
              : null,
          )
          if (SchemaRecordCounts.getValue()[schemaId] === undefined) {
            checkForFilteredRecords(schemaId, resp?.count)
          }
        }
      } catch (error) {
        if (error?.name === "AbortError" || signal?.aborted) return
        if (error.status === 413) {
          const errorMessage = await unrollError(error)
          addAlert(
            AlertType.ERROR,
            "System Limit Exceeded",
            errorMessage ||
              "Query exceeds system limits. Please use filters to narrow your query or reduce the number of text filter combinations.",
          )
        } else {
          addAlert(
            AlertType.ERROR,
            "Error fetching records",
            await unrollError(error),
          )
        }
      } finally {
        if (!signal?.aborted) {
          const ms = Math.round(performance.now() - t0)
          console.log(
            `%c⚡ [Blind Insight] Query roundtrip: ${ms}ms`,
            "color: #6366f1; font-weight: 600",
          )
          isLoadingRef.current = false
          setIsLoading(false)
        }
      }
    },
    [schemaId, currentFilters, limit, offset, api],
  )

  React.useEffect(() => {
    const controller = new AbortController()
    removeAlertBySource("Error fetching records")
    removeAlertBySource("Decrypted records not found")
    removeAlertBySource("Permission Denied")
    updateRecords(controller.signal)
    return () => controller.abort()
  }, [currentFilters, limit, offset, schemaId])

  // Remove the pagination dependency from the other effect
  React.useEffect(() => {
    if (isUploading === UploadStatus.COMPLETED) {
      updateRecords()
    }
  }, [isUploading])

  const [isOpen, setIsOpen] = React.useState(false)

  const clearStates = () => {
    if (isUploading === UploadStatus.COMPLETED) {
      updateRecords()
      setUploadProgress(0)
      setIsUploading(UploadStatus.NONE)
      setFileName(null)
    }
  }

  const openModal = () => {
    clearStates()
    setIsOpen(true)
  }
  const closeModal = () => {
    clearStates()
    removeAlertBySource("File Upload")
    clearSelectedFile()
    setIsOpen(false)
  }

  // TODO(jathan): Since this is a component, is it wise to use a callback?
  const EncryptButton = React.useCallback(() => {
    return (
      <>
        <IconLock
          locked="Danger.100"
          unlocked="Success.100"
          encrypted={encrypted.toString()}
        />
        <Button
          variant="secondary"
          size="xs"
          onClick={toggleEncryption}
          isDisabled={canDecryptRecords === false}
          title={
            canDecryptRecords
              ? "Toggle encryption"
              : "You must be an authorized data owner and decrypt locally using the proxy."
          }
        >
          {encrypted ? "Decrypt" : "Encrypt"}
        </Button>
      </>
    )
  }, [encrypted, canDecryptRecords, toggleEncryption])

  const [showBanner, setShowBanner] = React.useState(false)

  React.useEffect(() => {
    const dismissed = localStorage.getItem(SYSTEM_LIMITS_BANNER_KEY)
    if (!dismissed) {
      setShowBanner(true)
    }
  }, [])

  const handleDismissBanner = () => {
    localStorage.setItem(SYSTEM_LIMITS_BANNER_KEY, "true")
    setShowBanner(false)
  }

  return (
    <Flex direction="column" flexGrow={1} minHeight={0}>
      <HStack justifyContent="space-between" alignItems="flex-start">
        <Text>{schema.description}</Text>
        <HStack spacing={2}>
          <Button
            flexShrink={0}
            variant={schemaHasNoRecords ? "primary" : "secondary"}
            title="Upload Data to Blind Insight"
            size="xs"
            h={6}
            minH={6}
            px={2.5}
            fontSize="xs"
            rightIcon={
              isUploading === UploadStatus.UPLOADING ? (
                <CircularProgress size={"1.5em"} value={uploadProgress} />
              ) : (
                <FaUpload />
              )
            }
            onClick={openModal}
          >
            {isUploading === UploadStatus.UPLOADING ? "Uploading" : "Upload"}
          </Button>
        </HStack>
      </HStack>
      {showBanner && (
        <Alert
          status="info"
          borderRadius="md"
          position="relative"
          mt={4}
          mb={4}
        >
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>System Limits</AlertTitle>
            <AlertDescription>
              <Text fontSize="sm">
                <strong>Query Performance:</strong> Using more than{" "}
                {MAX_STRING_FILTER_COMBINATIONS} text filter combinations in a
                single query may result in slower performance. Consider reducing
                the number of text filters or using more specific values.
              </Text>
            </AlertDescription>
          </Box>
          <CloseButton
            alignSelf="flex-start"
            position="relative"
            right={-1}
            top={-1}
            onClick={handleDismissBanner}
          />
        </Alert>
      )}
      <Box>
        {hasProxy === false && (
          <Alert width="fit-content" status="info" variant="standard">
            <Text>
              Please{" "}
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                download and run the Blind Proxy
              </Link>{" "}
              to search and decrypt locally. Feel free to get in touch if you
              need assistance.
            </Text>
          </Alert>
        )}
        {hasProxy && <TableFilter />}
      </Box>
      <Flex direction="column" flexGrow={1} minHeight={0}>
        <DataTable
          schemaId={schemaId}
          EncryptButton={EncryptButton}
          hasProxy={hasProxy}
          data={data}
          columns={columns}
          rowCount={count}
          hasMore={hasMore}
          onUpdateRecords={updateRecords}
          isLoading={isLoading}
          isCountLoading={isCountLoading}
          onDeleteRow={(recordId) => {
            setDeleteRecordId(recordId)
            setDeleteRecordsOpen(true)
          }}
        />
      </Flex>
      <DeleteConfirmModal
        isOpen={deleteRecordsOpen}
        onClose={() => {
          setDeleteRecordsOpen(false)
          setDeleteRecordId(null)
          updateRecords()
        }}
        type="records"
        selectedIds={deleteRecordId ? [deleteRecordId] : []}
      />
      <Modal isOpen={isOpen} onClose={closeModal} variant="dataUpload">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <UploadRecords />
        </ModalContent>
      </Modal>
    </Flex>
  )
}
